/**
 * Modo apilado (móvil) de dashboards del Creador: ordena los widgets por su
 * posición en la grid y calcula un alto razonable para pintarlos en una sola
 * columna a todo el ancho, sin tocar el layout guardado.
 */
import { normalizeWidgetType, isContainerType, isChartType } from './engineConfig';

/* Alto mínimo por tipo al apilar, para que gráficas y tablas sigan siendo legibles. */
const MIN_HEIGHT = {
    kpi: 110,
    table: 260,
    map_mx: 300,
    slicer: 0,
    text: 0,
    image: 120,
    divider: 0,
};
const CHART_MIN = 260;

/**
 * Devuelve los widgets ordenados de arriba abajo y de izquierda a derecha
 * según su item de layout, junto con ese item.
 * @param {Array} widgets
 * @param {Array} layout Items de react-grid-layout ({ i, x, y, w, h }).
 * @returns {Array<{ widget: object, item: object }>}
 */
export function stackWidgets(widgets = [], layout = []) {
    const byId = new Map((layout || []).map((item) => [String(item.i), item]));
    return (widgets || [])
        .map((widget, index) => ({ widget, item: byId.get(String(widget.id)) || { x: 0, y: index, w: 1, h: 6 }, index }))
        .sort((a, b) => (a.item.y - b.item.y) || (a.item.x - b.item.x) || (a.index - b.index))
        .map(({ widget, item }) => ({ widget, item }));
}

/**
 * Alto en px de un widget apilado: el alto de su grid original con un mínimo
 * por tipo. Los bloques contenedores crecen con su contenido (`auto`).
 * @param {object} widget
 * @param {object} item Item de layout del widget.
 * @param {number} rowHeight Alto de fila del lienzo.
 * @param {number} gap Margen vertical entre filas.
 * @returns {number|'auto'}
 */
export function stackedHeight(widget, item, rowHeight, gap = 8) {
    if (isContainerType(widget.type)) return 'auto';
    const type = normalizeWidgetType(widget.type);
    const h = Math.max(1, Number(item?.h) || 1);
    const natural = h * rowHeight + (h - 1) * gap;
    const min = MIN_HEIGHT[type] ?? (isChartType(type) ? CHART_MIN : 0);
    return Math.max(min, natural);
}
